import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { throttle } from 'lodash';
import screenfull from 'screenfull';
import Controls from '../Controls';
import { AUDIO_TYPE, VIDEO_TYPE } from '../../consts';
import { sendMixPanelEvent } from '../../../Utils/mixpanel';
import { CLICKED_PLAYER_CONTROLS } from '../../../Utils/mixpanel/mixpanelEvents';
import { withContext } from '../../../Utils';
import Context, { contextPropType } from '../../context';
import FilerrShape from '../../../Previewer/PropsShape/FilerrShape';
import Utils from './utils';

import './style.scss';

const BUFFER_SIZE_IN_SECONDS = 2;

class AVPlayer extends Component {
    constructor(props) {
        super(props);

        this.state = {
            currentPosition: 0,
            totalLength: 0,
            isPlaying: false,
            isWaiting: false,
            isLoaded: true,
            isLoading: false,
            progressWidth: 0,
            bufferWidth: 0,
            volume: Utils.getVolume(),
            isInFullMode: false
        };

        this.player = null;

        // safePlayerQueue to play/pause.
        this.safePlayerQueue = Promise.resolve();
        this.progressBar = null;

        this.setPlayer = this.setPlayer.bind(this);
        this.handleLoad = this.handleLoad.bind(this);
        this.onEnded = this.onEnded.bind(this);
        this.onTimeUpdate = throttle(this.onTimeUpdate.bind(this), 50);
        this.progressClicked = this.progressClicked.bind(this);
        this.onProgress = this.onProgress.bind(this);
        this.seek = this.seek.bind(this);
        this.seekToPosition = this.seekToPosition.bind(this);
        this.getWidthForPosition = this.getWidthForPosition.bind(this);
        this.handleTogglePlay = this.handleTogglePlay.bind(this);
        this.play = this.play.bind(this);
        this.pause = this.pause.bind(this);
        this.onWaiting = this.onWaiting.bind(this);
        this.handleVolumeChange = this.handleVolumeChange.bind(this);
        this.toggleFullscreen = this.toggleFullscreen.bind(this);
        this.setCurrentTime = this.setCurrentTime.bind(this);
        this.isBufferEnoughtForPlaying = this.isBufferEnoughtForPlaying.bind(this);
        this.applySafePlayerQueue = this.applySafePlayerQueue.bind(this);
        this.setMarkerPosition = this.setMarkerPosition.bind(this);
        this.handleMarkerClick = this.handleMarkerClick.bind(this);
    }

    componentDidMount() {
        screenfull && screenfull.on('change', () => {
            this.setState({
                isInFullMode: screenfull.isFullscreen
            });
        });

        this.player.volume = this.state.volume;
    }

    componentDidUpdate(prevProps) {
        if (this.props.filerr !== prevProps.filerr) {
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({
                isPlaying: false,
                currentPosition: 0,
                progressWidth: 0
            });
        }
    }

    handleLoad(e) {
        if (!e) { return false; }

        const { duration } = e.target;

        this.setState({ totalLength: duration });

        const type = (this.player instanceof HTMLAudioElement) ? AUDIO_TYPE : VIDEO_TYPE;

        const { context } = this.props;

        context.onMediaLoad({
            type,
            element: this.player,
            api: {
                seek: (time) => { this.setCurrentTime(time); },
                play: this.play,
                pause: this.pause,
                currentTime: () => this.player.currentTime
            }
        });

        this.onProgress();
    }

    setPlayer(ref) {
        if (ref) {
            this.player = ref;
        }
    }

    onTimeUpdate() {
        const currentPosition = this.player.currentTime,
            progressWidth =this.getWidthForPosition(currentPosition);

        this.setState({
            progressWidth,
            currentPosition
        });
    }

    isBufferEnoughtForPlaying() {
        const { buffered, currentTime } = this.player;
        for (let i = 0; i < buffered.length; i++) {
            if (buffered.start(i) <= currentTime) {
                const bufferSize = (currentTime < this.player.duration - BUFFER_SIZE_IN_SECONDS) ? BUFFER_SIZE_IN_SECONDS : 0;
                if (buffered.end(i) > (currentTime + bufferSize)) {
                    return true;
                }
            }
        }

        return false;
    }

    applySafePlayerQueue(cb) {
        this.safePlayerQueue = this.safePlayerQueue.then(cb);
    }

    onProgress() {
        if (!this.player.buffered.length) { return; }

        const { isWaiting, isPlaying } = this.state;

        const bufferWidth = this.getWidthForPosition(
            this.player.buffered.end(this.player.buffered.length - 1)
        );

        if (isWaiting && isPlaying && this.isBufferEnoughtForPlaying()) {
            this.applySafePlayerQueue(() => this.player.play());
            this.setState({
                isWaiting: false
            });
        }

        this.setState({
            bufferWidth
        });
    }

    onEnded() {
        this.setState({ isPlaying: false });
    }

    toggleFullscreen() {
        if (typeof document === 'undefined' || !screenfull) { return; }

        if (screenfull.isFullscreen) {
            screenfull.exit();
            return;
        }

        if (screenfull.enabled) {
            screenfull.request(this.avRootRef);
        }

        this.sendToMixpanel('Full Screen');
    }

    setCurrentTime(currentTime) {
        this.player.currentTime = currentTime;
        const progressWidth = this.getWidthForPosition(this.player.currentTime);

        this.setState({
            progressWidth,
            currentPosition: currentTime
        });
    }

    progressClicked(e) {
        const { context } = this.props,
            rect = e.target.getBoundingClientRect(),
            left = e.clientX - rect.left,
            percentage = (left / this.progressBar.offsetWidth),
            totalTime = this.player.duration,
            currentTime = totalTime * percentage;

        this.setCurrentTime(currentTime);
        context.onProgressClick({ currentTime, totalTime });
    }

    seek(offset) {
        const { currentPosition } = this.state,
            nextPosition = currentPosition + offset;

        this.seekToPosition(nextPosition);
    }

    seekToPosition(nextPosition) {
        const { totalLength } = this.state;

        if (nextPosition < 0) {
            nextPosition = 0;
        } else if (nextPosition > totalLength) {
            nextPosition = totalLength;
        }

        this.setCurrentTime(nextPosition);

        this.sendToMixpanel('Seek');
    }

    getWidthForPosition(position) {
        return (position / this.player.duration) * 100;
    }

    handleTogglePlay(e) {
        e && e.preventDefault();

        if (!this.player) {return;}

        if (this.state.isPlaying) {
            this.sendToMixpanel('Pause');
            this.pause();

        } else {
            this.sendToMixpanel('Play');
            this.play();
        }
    }

    play() {
        const { context } = this.props;
        const { currentTime } = this.player;
        this.applySafePlayerQueue(() => this.player.play());
        context.onPlay({ currentTime });
        this.setState({
            isPlaying: true,
            isWaiting: false
        });
    }

    onWaiting() {
        if (!this.isBufferEnoughtForPlaying()) {
            // Buffering started
            this.applySafePlayerQueue(() => this.player.pause());
            this.setState({
                isWaiting: true
            });
        }
    }

    pause() {
        this.applySafePlayerQueue(() => this.player.pause());

        // This fixes the behaviour of inaccurate frame seeking when pausing video
        this.setCurrentTime(this.player.currentTime);

        this.setState({ isPlaying: false });
    }

    handleVolumeChange(volume) {
        if (!Utils.validVolume(volume)) {
            return;
        }

        const newVolume = parseFloat(volume.toFixed(2));
        this.player.volume = newVolume;

        this.setState({
            volume: newVolume
        }, () => Utils.setVolume(newVolume));
    }

    setMarkerPosition(timecode) {
        return (timecode / this.player.duration) * 100;
    }

    handleMarkerClick(marker) {
        this.props.context.onMarkerClick(marker);
    }

    sendToMixpanel(type) {
        const { filerr } = this.props;
        sendMixPanelEvent({
            eventName: CLICKED_PLAYER_CONTROLS,
            eventData: {
                'Action Type': type,
                'File Type': filerr.previewType,
                'Duration': this.player.duration,
                'File Width': this.player.clientWidth,
                'File Height': this.player.clientHeight
            }
        });
    }

    render() {
        const {
            volume,
            isPlaying,
            currentPosition,
            totalLength,
            progressWidth,
            bufferWidth,
            isInFullMode,
            isWaiting
        } = this.state;

        const { children, context } = this.props;
        const { markers, markersTemplate } = context;

        const hasMarkers = markers && !!markers.length;
        const isVideo = !!(this.player && this.player instanceof HTMLVideoElement);

        return (
            <div className={`audio-video-player ${isInFullMode ? 'full-mode' : ''}`}
                ref={(avRootRef) => this.avRootRef = avRootRef}>
                {isPlaying && isWaiting && <div className="loading"></div>}
                {children(
                    this.onTimeUpdate,
                    this.onProgress,
                    this.onEnded,
                    this.handleLoad,
                    this.setPlayer,
                    this.onWaiting
                )}
                <div className="wrap-bottom-bar">
                    <div className="bottom-bar">
                        <div className="progress">
                            {hasMarkers && <span className="markers-background"/>}
                            {hasMarkers && this.player && markers.map((marker) => {
                                const position = this.setMarkerPosition(marker.timecode);
                                const style = {
                                    left: `${position}%`
                                };

                                return (
                                    <span key={marker.id} className="marker" style={style} onClick={() => this.handleMarkerClick(marker)}>
                                        {markersTemplate(marker, this.state, this.player)}
                                    </span>
                                );
                            })}
                            <div className="progress-click" onClick={this.progressClicked}>
                                <div className="progress-line" ref={(ref) => this.progressBar = ref}>
                                    <div className="progress-buffer-line" style={{ width: `${bufferWidth}%` }}></div>
                                    <div className="progress-green-line" style={{ width: `${progressWidth}%` }}></div>
                                </div>
                            </div>
                        </div>
                        <Controls isPlaying={isPlaying}
                            isVideo={isVideo}
                            volume={volume}
                            onSeek={this.seek}
                            currentPosition={currentPosition}
                            totalLength={totalLength}
                            onVolumeChange={this.handleVolumeChange}
                            toggleFullscreen={isVideo ? this.toggleFullscreen : null}
                            onPlayPause={this.handleTogglePlay}/>
                    </div>
                </div>
            </div>
        );
    }
}

AVPlayer.propTypes = {
    filerr: FilerrShape.isRequired,
    children: PropTypes.func.isRequired,
    context: contextPropType
};

const mapContextToProps = (context) => ({
    context
});

export default withContext(Context, mapContextToProps, AVPlayer);
